import { zodResolver } from "@hookform/resolvers/zod";
import { Check, MessageCircle, Send } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildWhatsAppUrl, businessTypes, faqItems, leadInterests } from "@/lib/landing-config";

const leadSchema = z.object({
  establishmentName: z.string().trim().min(2, "Informe o nome do estabelecimento."),
  contactName: z.string().trim().min(2, "Informe seu nome."),
  whatsapp: z
    .string()
    .trim()
    .refine(
      (value) => /^(?:[1-9]{2})9?\d{8}$/.test(value.replace(/\D/g, "")),
      "Informe um WhatsApp válido.",
    ),
  businessType: z.string().min(1, "Selecione o tipo do negócio."),
  interest: z.string().min(1, "Selecione como podemos ajudar."),
});

type LeadFormData = z.infer<typeof leadSchema>;

const defaultValues: LeadFormData = {
  establishmentName: "",
  contactName: "",
  whatsapp: "",
  businessType: "",
  interest: "",
};

export function LeadForm({ compact = false }: { compact?: boolean }) {
  const [submitted, setSubmitted] = useState(false);
  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  function onSubmit(data: LeadFormData) {
    const businessType =
      businessTypes.find((item) => item.value === data.businessType)?.label ?? data.businessType;
    const interest =
      leadInterests.find((item) => item.value === data.interest)?.label ?? data.interest;
    const url = buildWhatsAppUrl({ ...data, businessType, interest });

    if (!url) {
      form.setError("root", {
        type: "config",
        message: "O WhatsApp de atendimento ainda não foi configurado.",
      });
      return;
    }

    const popup = window.open(url, "_blank", "noopener,noreferrer");
    if (!popup) {
      form.setError("root", {
        type: "popup",
        message: "Permita pop-ups para abrir o atendimento pelo WhatsApp.",
      });
      return;
    }

    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-start gap-4 rounded-2xl border border-primary/20 bg-primary/10 p-5 text-left sm:p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Mensagem pronta para enviar.</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            O WhatsApp foi aberto com os dados do seu restaurante. Se ele não abriu, tente
            novamente.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={() => setSubmitted(false)}>
          Preencher novamente
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="establishmentName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome do estabelecimento</FormLabel>
                <FormControl>
                  <Input
                    className="min-h-11"
                    placeholder="Ex.: Brasa Burger"
                    autoComplete="organization"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="sr-only">Campo obrigatório.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Seu nome</FormLabel>
                <FormControl>
                  <Input
                    className="min-h-11"
                    placeholder="Como podemos chamar você?"
                    autoComplete="name"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="sr-only">Campo obrigatório.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="whatsapp"
          render={({ field }) => (
            <FormItem>
              <FormLabel>WhatsApp</FormLabel>
              <FormControl>
                <Input
                  className="min-h-11"
                  placeholder="(11) 99999-0000"
                  inputMode="tel"
                  autoComplete="tel"
                  {...field}
                />
              </FormControl>
              <FormDescription className="sr-only">Campo obrigatório.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="businessType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de negócio</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  onOpenChange={() => field.onBlur()}
                >
                  <FormControl>
                    <SelectTrigger className="min-h-11">
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white text-slate-950">
                    {businessTypes.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription className="sr-only">Campo obrigatório.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="interest"
            render={({ field }) => (
              <FormItem>
                <FormLabel>O que você quer resolver?</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  onOpenChange={() => field.onBlur()}
                >
                  <FormControl>
                    <SelectTrigger className="min-h-11">
                      <SelectValue placeholder="Escolha um foco" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white text-slate-950">
                    {leadInterests.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription className="sr-only">Campo obrigatório.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" size="lg" className="w-full gap-2 sm:w-auto">
          <Send className="h-4 w-4" />
          {compact ? "Falar com a Innovally" : "Continuar pelo WhatsApp"}
        </Button>
        {form.formState.errors.root?.message ? (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        ) : null}
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <MessageCircle className="h-3.5 w-3.5 text-primary" />
          Atendimento inicial direto com uma pessoa do time Innovally.
        </p>
      </form>
    </Form>
  );
}

export function FaqList() {
  return (
    <Accordion type="single" collapsible className="w-full">
      {faqItems.map((item, index) => (
        <AccordionItem key={item.question} value={`faq-${index}`}>
          <AccordionTrigger>{item.question}</AccordionTrigger>
          <AccordionContent>{item.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
